import {
  ExternalHyperlink,
  HeadingLevel,
  ImageRun,
  Paragraph,
  TextRun
} from 'docx'
import logoSrc from '../../assets/fd-logo.png'
import { formatMarketLine } from '../../modules/Questionnaire/MarketVisibility/useHiddenMarketsLabel'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import dayjs from '../dayjs'
import { PreviewLink } from './exportMarkets'

interface SummaryParams {
  projectName: string
  surveyId: string
  countryName: string
  previewUrl: string
  // Authoritative multi-market flag (from project.projectType). When true the
  // summary lists every market instead of a single (misleading) country, since
  // the exported script is the shared questionnaire across all of them. Kept
  // separate from `markets` so a cold-cache (empty) market list never downgrades
  // the decision back to a single-country line.
  isMultiMarket?: boolean
  // The full market list, rendered as the "Markets:" line when isMultiMarket.
  markets?: MarketOption[]
  // Per-market preview links, rendered as a labelled list when isMultiMarket.
  // Falls back to the single `previewUrl` when absent/empty (single-market).
  previewLinks?: PreviewLink[]
}

// Multi-market: one labelled hyperlink per market ("🇫🇷 France: <url>"), under a
// "Preview links:" header. Otherwise the single "Preview link:" hyperlink.
export const buildPreviewSection = (
  previewUrl: string,
  previewLinks?: PreviewLink[],
  isMultiMarket = false
): Paragraph[] => {
  if (isMultiMarket && previewLinks && previewLinks.length > 0) {
    return [
      new Paragraph('Preview links:'),
      ...previewLinks.map(
        (link, index) =>
          new Paragraph({
            children: [
              new TextRun(`${link.label}: `),
              new ExternalHyperlink({
                children: [new TextRun({ text: link.url, style: 'Hyperlink' })],
                link: link.url
              })
            ],
            spacing:
              index === previewLinks.length - 1 ? { after: 400 } : undefined
          })
      )
    ]
  }

  return [
    new Paragraph({
      children: [
        new ExternalHyperlink({
          children: [
            new TextRun('Preview link: '),
            new TextRun({
              text: previewUrl,
              style: 'Hyperlink'
            })
          ],
          link: previewUrl
        })
      ],
      spacing: {
        after: 400
      }
    })
  ]
}

export const createSummary = async ({
  projectName,
  surveyId,
  countryName,
  previewUrl,
  isMultiMarket = false,
  markets = [],
  previewLinks
}: SummaryParams) => {
  const marketsParagraph = isMultiMarket
    ? new Paragraph(`Markets: ${markets.map(formatMarketLine).join(', ')}`)
    : new Paragraph(`Country: ${countryName}`)
  // PNG rather than the SVG asset: Word cannot render an SVG without a raster
  // fallback embedded alongside it, and the old code supplied neither, which
  // is why the logo has never appeared in an exported document.
  const logo = await fetch(logoSrc).then((r) => r.arrayBuffer())
  const currentDateAndTimeFormatted = dayjs().format('HH:mm DD MMMM YYYY')
  const utcOffsetMinutes = dayjs().utcOffset()
  const utcOffsetFormatted = `${utcOffsetMinutes < 0 ? '' : '+'}${
    utcOffsetMinutes / 60
  }`

  return [
    new Paragraph({
      children: [
        new ImageRun({
          type: 'png',
          data: logo,
          transformation: {
            width: 252,
            height: 38
          }
        })
      ],
      spacing: {
        after: 300
      }
    }),
    new Paragraph({
      text: projectName.toUpperCase(),
      heading: HeadingLevel.HEADING_2
    }),
    new Paragraph(`Survey ID: ${surveyId}`),
    new Paragraph(
      `Script exported: ${currentDateAndTimeFormatted} (UTC ${utcOffsetFormatted})`
    ),
    marketsParagraph,
    ...buildPreviewSection(previewUrl, previewLinks, isMultiMarket)
  ]
}
